/*
Author: Gilang Romadhanu Tartila
*/

import React from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import WebViews from '@/plugins/webview';

import Colors from '@global/colors';
import { isWeb, isNative } from '@global/screens';
import { getEnvironment } from '@global/helpers';

const Config = getEnvironment();

const propTypes = {
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  height: PropTypes.number,
  type: PropTypes.string,
  data: PropTypes.array,
  categories: PropTypes.array,
  colors: PropTypes.array,
  showCategories: PropTypes.bool,
  // columnStackingOption: PropTypes.string,
  tooltipYFormat: PropTypes.string,
  pieTooltipFormat: PropTypes.string,
  innerSize: PropTypes.string,
  showDataLabels: PropTypes.bool,
  // Column series side by side rather than overlaid. Off by default: the historical setting.
  grouping: PropTypes.bool,
  // Text drawn in the middle of the plot. Meant for the hole of a donut.
  centerText: PropTypes.string,
  // Column data labels, off unless a format is given. A Highcharts format string; custom
  // point properties are reachable as {point.<name>}.
  dataLabelFormat: PropTypes.string,
  dataLabelColor: PropTypes.string,
};

const defaultProps = {
  style: {},
  height: 400,
  type: 'column',
  data: [],
  categories: [],
  colors: [],
  showCategories: true,
  // columnStackingOption: '',
  tooltipYFormat: '',
  pieTooltipFormat: '',
  innerSize: '',
  showDataLabels: true,
  grouping: false,
  centerText: '',
  dataLabelFormat: '',
  dataLabelColor: '',
};

const Highcharts = (props) => {
  const {
    style = {},
    height = 400,
    type = 'column',
    data = [],
    categories = [],
    colors = [],
    showCategories = true,
    // columnStackingOption = '',
    tooltipYFormat = '',
    pieTooltipFormat = '',
    innerSize = '',
    showDataLabels = true,
    grouping = false,
    centerText = '',
    dataLabelFormat = '',
    dataLabelColor = '',
  } = props;

  const html = `
		<!DOCTYPE html>
		<html>
		<head>
			<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
			<style>
				* { box-sizing: border-box; margin: 0; padding: 0; }
				html, body { width: 100%; height: 100%; overflow: hidden; }
				#highchart-container { width: 100%; }
			</style>
		</head>
		<body>
		<script src="https://code.highcharts.com/highcharts.js"></script>

		<div id="highchart-container" class="highcharts-light"></div>

		<script>
			Highcharts.setOptions({
				lang: {
					numericSymbols: ['K', 'M', 'B', 'T', 'P', 'E']
				}
			});

			Highcharts.chart('highchart-container', {
				chart: {
					type: '${type}',
					height: ${height},
					${
            // The pie reports its own centre (series.center, relative to the plot area), so
            // the label is placed exactly there on every render, whatever the layout does.
            // dominant-baseline centres the glyphs on that point; a title's y offset only
            // ever approximated it.
            centerText
              ? `events: {
						render: function () {
							var chart = this;
							var series = chart.series[0];
							if (!series || !series.center) { return; }
							var x = chart.plotLeft + series.center[0];
							var y = chart.plotTop + series.center[1];
							if (!chart.centerLabel) {
								chart.centerLabel = chart.renderer
									.text(${JSON.stringify(centerText)}, x, y)
									.attr({ align: 'center', 'dominant-baseline': 'central', zIndex: 5 })
									.css({
										fontSize: '${isWeb ? '28px' : '24px'}',
										fontWeight: 'bold',
										color: '${Colors.textSecondary}'
									})
									.add();
							} else {
								chart.centerLabel.attr({ x: x, y: y });
							}
						}
					},`
              : ''
          }
					${
            type == 'pie'
              ? `
						plotBackgroundColor: null,
						plotBorderWidth: null,
						plotShadow: false,
					`
              : ``
          }
				},

				xAxis: {
					categories: ${JSON.stringify(categories)},
				},

				yAxis: {
					title: {
						text: ''
					},
					${
            // Headroom above the tallest column so its label sits over the bar rather than
            // inside it. Only when labels are drawn: the default 0.05 stays for other charts.
            dataLabelFormat ? 'maxPadding: 0.15,' : ''
          }
				},

				title: {
					text: ''
				},

				tooltip: {
					${
            type == 'pie'
              ? pieTooltipFormat
                ? `headerFormat: '',`
                : ''
              : `headerFormat: '<span style="font-size:10px">{point.key}</span><table>',`
          }
					pointFormat: ${
            type == 'pie'
              ? `
						'${
              pieTooltipFormat ||
              '{series.name}: <b><br>{point.y}<br>{point.percentage_value:.0f}%</b>'
            }'
					`
              : `
						'<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
						'<td style="padding:0"><b>${
              tooltipYFormat ? tooltipYFormat : '{point.y}'
            }</b></td></tr>'
					`
          },
					${
            type == 'pie'
              ? ''
              : `
						footerFormat: '</table>',
						shared: true,
						useHTML: true
					`
          }

				},

				legend: {
					symbolRadius: 0
				},

				plotOptions: {
					column: {
						stacking: null,
						grouping: ${grouping},
						 pointPadding: 0.1,
						borderWidth: 0,
						dataLabels: {
							${
                dataLabelFormat
                  ? `enabled: true, format: ${JSON.stringify(dataLabelFormat)},`
                  : ''
              }
							style: {
								fontSize: ${isWeb ? '11' : '14'},
								textShadow: false,
								textOutline: 'none'${
                  dataLabelColor
                    ? `,
								color: '${dataLabelColor}'`
                    : ''
                }
							}
						},
						showInLegend: ${showCategories}
					},
					line: {
						dataLabels: {
							style: {
								fontSize: ${isWeb ? '11' : '14'},
								textShadow: false,
								textOutline: 'none'
							}
						},
						showInLegend: ${showCategories}
					},
					pie: {
						allowPointSelect: true,
						cursor: 'pointer',
						${innerSize ? `innerSize: '${innerSize}',` : ''}
						dataLabels: {
							enabled: ${showDataLabels},
							format: '{point.percentage_value:.0f}%',
							distance: -40,
							style: {
								fontSize: ${isWeb ? '16' : '20'},
								textShadow: false,
								textOutline: 'none',
								color: '#FFFFFF'
							}
						}
					}
				},

				colors: ${
          colors?.length
            ? `${JSON.stringify(colors)}`
            : `['${Colors.blue0}','${Colors.yellow0}','${Colors.green1}']`
        },

				series: ${JSON.stringify(data)}
			});
		</script>
		</body>
		</html>
	`;

  return (
    <View style={[styles.container, style]}>
      <WebViews
        originWhitelist={['*']}
        source={{ html, baseUrl: Config?.B2B_WEB_URL }}
        javaScriptEnabled={true}
        domStorageEnabled={true}
        startInLoadingState={true}
        style={{ ...styles.wrapper, height }}
        androidHardwareAccelerationDisabled={true}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    ...(isNative
      ? {
          flex: 1,
        }
      : {}),
  },
  wrapper: {
    width: '100%',
  },
});

Highcharts.propTypes = propTypes;
Highcharts.defaultProps = defaultProps;

export default React.memo(Highcharts, (prev, next) => prev == next);
